import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Lock, MapPin, Users } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import { BlogFooter } from "../blog/BlogPages";
import { api, isGiggleNull } from "../rsvp/api";
import "../blog/blog.css";
import "../styles/Plans.css";

const APP_STORE = "https://apps.apple.com/us/app/giggle-make-friends-chat/id6498945325";
const PLAY_STORE = "https://play.google.com/store/apps/details?id=com.schaffen.giggle";

/** "Today · Anytime", "Sat 25 Oct · 7:30 PM": in the plan's own time zone. */
export function whenText(plan) {
  const [y, m, d] = (plan.plan_date || "").split("-").map(Number);
  const day = y ? new Date(y, m - 1, d) : null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let dayText = plan.plan_date;
  if (day) {
    const diff = Math.round((day - today) / 86400000);
    dayText = diff === 0 ? "Today" : diff === 1 ? "Tomorrow" : day.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  }
  let timeText = "Anytime";
  if (plan.time_mode === "specific" && plan.starts_at) {
    timeText = new Date(plan.starts_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: plan.time_zone || undefined });
  }
  return `${dayText} · ${timeText}`;
}

export function whereText(plan) {
  if (plan.location_mode === "specific" && plan.place_name) return plan.place_name;
  if (plan.area_name) return `Around ${plan.area_name}`;
  return plan.city || "Somewhere nearby";
}

/** /plan/:id: a plan someone shared. Joining and the chat happen in the app. */
const PlanPage = () => {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.publicPlan(Number(planId))
      .then(({ data }) => {
        setPlan(data.plan);
        document.title = `I want to ${data.plan.title} · Blox`;
      })
      .catch((e) => setError(e.status === 404 ? "This plan isn't available any more." : "We couldn't load this plan. Please refresh in a moment."));
  }, [planId]);

  // On a phone with Blox installed, try the app first; the page stays as the fallback.
  const openInApp = () => { window.location.href = `blox://plan/${planId}`; };

  const pic = plan?.host?.picture && !isGiggleNull(plan.host.picture) ? plan.host.picture : null;
  const ages = plan && (plan.max_age >= 80 ? (plan.min_age <= 18 ? "All ages 18+" : `Ages ${plan.min_age}+`) : `Ages ${plan.min_age}–${plan.max_age}`);
  const live = plan?.status === "active";
  const full = live && plan.max_members && plan.member_count >= plan.max_members;

  return (
    <main className="blog-page">
      <SiteHeader />
      <section className="plan-wrap">
        {error ? (
          <p className="plan-empty">{error}</p>
        ) : !plan ? (
          <div className="plan-card plan-card-loading" />
        ) : (
          <article className="plan-card">
            {!live && <p className="plan-banner">{plan.status === "cancelled" ? "This plan was cancelled" : "This plan has ended"}</p>}
            <div className="plan-emoji">{plan.category?.emoji || "✨"}</div>
            <p className="plan-category">{plan.category?.name}</p>
            <h1>I want to {plan.title}</h1>
            {plan.description && <p className="plan-description">{plan.description}</p>}
            <ul className="plan-facts">
              <li><CalendarDays size={18} /> {whenText(plan)}</li>
              <li><MapPin size={18} /> {whereText(plan)}</li>
              <li>{plan.join_mode === "approval" ? <Lock size={18} /> : <Users size={18} />} {plan.join_mode === "approval" ? "Host approves who joins" : "Anyone can join"} · {ages}</li>
              <li><Users size={18} /> {plan.member_count} going{plan.max_members ? ` · ${plan.max_members} max` : ""}</li>
            </ul>
            <div className="plan-host">
              {pic ? <img src={pic} alt="" /> : <span>{(plan.host?.first_name || "?").charAt(0).toUpperCase()}</span>}
              <div><small>Hosted by</small><strong>{plan.host?.first_name || "Someone on Blox"}</strong></div>
            </div>
            {live && (
              <>
                {full && <p className="plan-banner plan-banner-full">This plan is full, but you can join the waitlist in the app. If a spot opens up, you're in.</p>}
                <button type="button" className="plan-open" onClick={openInApp}>{full ? "Join the waitlist in Blox" : "Open in the Blox app"}</button>
                <p className="plan-note">Don't have Blox yet? Get it, then open this link again to {full ? "join the waitlist" : "join and chat"}.</p>
                <div className="plan-stores">
                  <a href={APP_STORE} target="_blank" rel="noreferrer">App Store</a>
                  <a href={PLAY_STORE} target="_blank" rel="noreferrer">Google Play</a>
                </div>
              </>
            )}
          </article>
        )}
      </section>
      <BlogFooter />
    </main>
  );
};

export default PlanPage;
